'use client';

import { useEffect } from 'react';
import { useTheme } from 'next-themes';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import type { ColorSchemeData } from '@nf/types/site';
import { Maybe, MaybeMatch } from '@nf/utils-common/fpw/monadic';
import queryKeys from '@/constants/query-keys';
import { siteService } from '@/services/site.service';
import { useSiteStore } from '@/store/site';
import { useAppearanceStore } from '@/store/appearance';
import { useColorSchemeStore } from '@/store/color-schema';
import { ViewEnum } from '@/types';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';

const COLOR_SCHEME_STYLE_ID = 'nf-color-scheme-vars';

function convertStyleText(colorScheme: ColorSchemeData[0]): string {
	if (colorScheme === undefined) {
		return '';
	}
	return Object.entries(colorScheme)
		.filter(([key]) => key !== 'siteTheme')
		.map(([key, value]) => `--${key}: ${value};`)
		.join('');
}

function buildColorSchemeCss(
	view: ViewEnum,
	darkScheme: ColorSchemeData[0],
	lightScheme: ColorSchemeData[0],
): string {
	const dark = convertStyleText(darkScheme);
	const light = convertStyleText(lightScheme);
	if (view === ViewEnum.mars) {
		return `:root:not(#\\#):not(#\\#) { ${dark} }`;
	}
	return `:root[data-view="galaxy"] { ${dark} ${light} }`;
}

export const ColorSchemeProvider = () => {
	const { theme } = useTheme();
	const [skinColor, skinMode] = useSiteStore(
		useShallow(state => [state.deepLinkSiteInfo.skinColor, state.deepLinkSiteInfo.skinMode]),
	);
	const [colorSchemeData, setColorSchemeData, setIsLoading] = useColorSchemeStore(state => [
		state.colorSchemeData,
		state.setColorSchemeData,
		state.setIsLoading,
	]);
	const [darkScheme, lightScheme] = colorSchemeData;
	const view = useAppearanceStore(state => state.view);
	const { SiteConfigsData: siteConfigData } = useGetSiteConfigs();

	useEffect(() => {
		siteConfigData?.GetMessageID && document.documentElement.setAttribute('data-site-id', siteConfigData.GetMessageID.substring(0, 5));
	}, [siteConfigData?.GetMessageID]);

	const colorSchemeQuery = useQuery({
		queryKey: [queryKeys.COLOR_SCHEME, skinColor, view],
		queryFn: () => siteService.fetchColorScheme(skinColor, view),
		enabled: skinMode !== 0,
		staleTime: Infinity,
		cacheTime: Infinity,
		onError: err => {
			console.error('getColorScheme Error:', err);
		},
	});

	useEffect(() => {
		setIsLoading(colorSchemeQuery.isFetching);
	}, [colorSchemeQuery.isFetching, setIsLoading]);

	useEffect(() => {
		const data = colorSchemeQuery.data;
		if (!data) {
			return;
		}
		const schemeResult = data;
		const [darkSchemeEntity] = schemeResult.Data ?? [];
		Maybe(darkSchemeEntity)
			.chain(() => MaybeMatch(theme === 'dark'))
			.fold(
				() => { },
				() => document.documentElement.setAttribute('data-site-theme', darkSchemeEntity.siteTheme)
			);
		setColorSchemeData(schemeResult.Data ?? []);
	}, [colorSchemeQuery.data, theme, setColorSchemeData]);

	useEffect(() => {
		if (colorSchemeData.length === 0) {
			return;
		}

		const css = buildColorSchemeCss(view, darkScheme, lightScheme);
		let el = document.getElementById(COLOR_SCHEME_STYLE_ID) as HTMLStyleElement | null;
		if (!el) {
			el = document.createElement('style');
			el.id = COLOR_SCHEME_STYLE_ID;
			document.head.appendChild(el);
		}
		el.setAttribute('data-styled-jsx', '');
		el.textContent = css;

		return () => {
			document.getElementById(COLOR_SCHEME_STYLE_ID)?.remove();
		};
	}, [colorSchemeData, darkScheme, lightScheme, view]);

	return null;
};